import PropTypes from "prop-types";

import { useCallback, useEffect, useRef, useContext } from "react";

import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { errorDescribedBy } from "~/components/Base/ValidationError/errorDescribedBy";
import {
  hasLabelableControl,
  isStaticItemType,
  isTextItem,
} from "../shared/form-helpers";
import { useHashScroll } from "~/hooks/useHashScroll";
import { BusyStateContext } from "~/context/BusyStateContext";

import {
  BoxSection,
  BoxSectionBackground,
} from "~/components/Base/Box/BoxSection/BoxSection";
import {
  FormItemLabel,
  formItemLabelId,
} from "./FormItemLabel/FormItemLabel";
import { ValidationError } from "~/components/Base/ValidationError/ValidationError";

import "./FormItem.css";
import classNames from "classnames";

/**
 * react-hook-form cannot store `undefined`: its internal `get()` treats a
 * stored `undefined` as "absent" and falls back to the field's defaultValue,
 * which `Controller` captures when the item mounts. An item cleared after its
 * item group was re-entered would therefore snap back to whatever value the
 * group mounted with instead of clearing (#104074 — the date picker's Reset
 * button, which emits `undefined`).
 *
 * `null` round-trips through RHF unchanged and is already the empty value this
 * form uses for non-checkbox items (see `getDefaultItemValue`).
 */
const toStoredValue = (value) => (value === undefined ? null : value);

/**
 * All messages of the RHF field error, one per failing rule. With
 * `criteriaMode: "all"` every failing rule lands in `error.types`
 * (values that aren't strings — e.g. `true` from message-less
 * validators — are dropped). Falls back to the single `error.message`
 * for forms validated with the default firstError mode.
 */
const getErrorMessages = (error) => {
  if (!error) {
    return [];
  }

  const messages = [
    ...new Set(
      Object.values(error.types ?? {}).filter(
        (message) => typeof message === "string"
      )
    ),
  ];

  return messages.length > 0 ? messages : [error.message];
};

export function FormItem({
  control,
  error,
  htmlType,
  itemType,
  label,
  oid,
  onChange,
  onReset,
  readOnly,
  render,
  validation,
  value,
  ...props
}) {
  const { t } = useTranslation();

  // Context to handle busy form items and idle timer
  const { addBusyItem, removeBusyItem } = useContext(BusyStateContext);

  const itemRef = useRef();
  const debounceTimer = useRef();
  const isBusy = useRef(false);

  useHashScroll(itemRef, oid);

  const shouldDebounce = (itemType) =>
    isTextItem(itemType) || itemType === "VasScale";

  const onItemValueChange = (value, format, onValidatedChange, isReset) => {
    const storedValue = toStoredValue(value);

    if (isReset) {
      // The item's own empty value goes with the reset so react-hook-form
      // stores it instead of falling back to the field's mount-time default.
      onReset(oid, storedValue);
    } else {
      onValidatedChange(storedValue);
    }

    if (shouldDebounce(itemType)) {
      onDebouncedChange(oid, storedValue, format);
    } else {
      onChange(oid, storedValue, format);
    }
  };

  const onDebouncedChange = (oid, value, format) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      onChange(oid, value, format);
    }, 500);
  };

  // Keep track of busy items to pause/resume idle timer
  const setBusy = useCallback(
    (itemBusy) => {
      // Early return if value did not change
      if (isBusy.current === itemBusy) {
        return;
      }

      isBusy.current = itemBusy;

      // Include missing busy item
      if (itemBusy) {
        addBusyItem(oid);
        return;
      }

      // Remove existing non-busy item
      if (!itemBusy) {
        removeBusyItem(oid);
      }
    },
    [oid, addBusyItem, removeBusyItem]
  );

  useEffect(() => {
    // Ensure to resume idle timer if navigated away during upload
    return function cleanup() {
      removeBusyItem(oid);
    };
  }, [oid, removeBusyItem]);

  const getIsReady = () => {
    if (!isBusy.current) {
      return true;
    }

    return itemType === "FileUpload"
      ? t("form.file_upload.busy")
      : t("form.busy");
  };

  // Append ready validation rule
  // This is done here and not in convertFormValidation since it depends on item state and not item value
  const rules =
    validation == null
      ? null
      : {
          ...validation,
          validate: {
            ...validation.validate,
            ready: getIsReady,
          },
        };

  const backgroundColor = isStaticItemType(itemType)
    ? BoxSectionBackground.GREY
    : BoxSectionBackground.WHITE;

  const labelTargetsControl = hasLabelableControl({
    itemType,
    htmlType,
    readOnly,
  });

  const RenderComponent = ({
    field: { ref, onChange: onValidatedChange, value, ...rest },
  }) => {
    const handleChange = useCallback(
      (value, format, isReset) =>
        onItemValueChange(value, format, onValidatedChange, isReset),
      [onValidatedChange]
    );

    return render({
      ariaDescribedBy: errorDescribedBy(oid),
      ariaLabelledBy: labelTargetsControl ? undefined : formItemLabelId(oid),
      onChange: handleChange,
      onBusy: setBusy,
      readOnly,
      valid: !error,
      value,
    });
  };

  return (
    <BoxSection backgroundColor={backgroundColor} {...props}>
      {/* data-oid instead of id: the field itself carries id={oid}, targeted
          either by the label's htmlFor (native controls) or by aria-labelledby
          (composite widgets); scrollToItem falls back to data-oid. */}
      <div
        data-oid={oid}
        className={classNames({
          "form-item": true,
          "form-item--static": isStaticItemType(itemType),
        })}
        ref={itemRef}
      >
        {!isStaticItemType(itemType) && (
          <FormItemLabel
            oid={oid}
            label={label}
            targetsControl={labelTargetsControl}
          />
        )}
        {isStaticItemType(itemType) ? (
          render({})
        ) : (
          <>
            <Controller
              control={control}
              defaultValue={value}
              key={oid}
              name={oid}
              rules={rules}
              render={RenderComponent}
            />
            <ValidationError
              id={errorDescribedBy(oid)}
              messages={getErrorMessages(error)}
            />
          </>
        )}
      </div>
    </BoxSection>
  );
}

FormItem.propTypes = {
  control: PropTypes.object,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  htmlType: PropTypes.string,
  itemType: PropTypes.string,
  label: PropTypes.string,
  oid: PropTypes.string,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  readOnly: PropTypes.bool,
  render: PropTypes.func,
  validation: PropTypes.object,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
};
